import {
  memo,
  useEffect,
  useEffectEvent,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { type Observable, debounceTime, map, merge } from 'rxjs';
import { isEqual } from 'lodash';
import { ViewIds } from '@ardoq/api-types';
import type { ChartWindowArgs } from '@ardoq/graph';
import {
  type Action,
  type ReducerArray,
  action$,
  ofType,
  reducer,
  withNamespace,
} from '@ardoq/rxbeach';
import { clearGraphSelection } from '@ardoq/component-selection';
import { setIsViewExporting, viewExportRequested } from '@ardoq/export/state';
import {
  chartAreaScrollTopChanged,
  collapseExpandAllFieldData,
  collapseExpandAllGroups,
  collapseExpandFieldData,
  collapseExpandGroup,
  setChartWindowState,
  setFocusedItem,
  setHoveredItem,
  setResizeIsActive,
  setResizeIsInactive,
  setTimelineChartWindowScrollPosition,
  setTimelineMouseHover,
  setUserDefinedLabelsColumnWidth,
} from './actions';
import {
  MouseHoverEventStreamState,
  TimelineChartPropertiesState,
  TimelineRenderingCommands,
  TimelineRenderingState,
  TimelineSourceData,
  TimelineViewCommands,
  TimelineViewComponents,
  TimelineUiOnlyViewSettings,
  TimelineViewModelState,
  TimelineViewSettingsControlsHandlers,
  ViewModelSourceData,
} from './types';
import { timelineViewModelOperations } from './data/viewModelOperations';
import { sortViewModelSourceDataHierarchy } from './data/calculations';
import {
  extractCollapsedState,
  hasRelevantCollapsedStateUpdate,
} from './data/viewModelUtils';
import { DEFAULT_TIMELINE_VIEW_MODEL_STATE } from './data/consts';
import { DEFAULT_CHART_WINDOW_STATE } from './consts';
import { Timeline } from './Timeline';

export type TimelineRenderingLayerStaticProps = TimelineViewComponents & {
  viewInstanceId: string;
  settingsControlsHandlers?: TimelineViewSettingsControlsHandlers;
};

export type TimelineRenderingLayerViewSettingsProps = {
  uiOnlyViewSettings: TimelineUiOnlyViewSettings;
};

export type TimelineRenderingLayerStreamedProps =
  TimelineRenderingLayerViewSettingsProps & {
    sourceData: ViewModelSourceData;
    commands: TimelineViewCommands;
  };

export type TimelineRenderingLayerProps = TimelineRenderingLayerStaticProps &
  TimelineRenderingLayerStreamedProps;

const LABELS_COLUMN_WIDTH_DEBOUNCE_TIME = 100;
const COLUMNS_RESIZE_DEACTIVATION_DEBOUNCE_TIME = 200;

const INITIAL_CHART_PROPERTIES: TimelineChartPropertiesState = {
  clientRect: { width: 0, height: 0 },
  userDefinedLabelsColumnWidth: 0,
  getTimelineContentContainer: () => null,
};

const INITIAL_MOUSE_HOVER: MouseHoverEventStreamState = {
  isResizeActive: false,
  crosshairXPosition: -1,
  graphNodeId: null,
  itemPhasesRowIndex: null,
  otherLabelIndex: null,
  popoverElementType: null,
  popoverY: 0,
  popoverX: 0,
};

const updateCollapsedSettingsFromState = (
  previousState: TimelineViewModelState,
  state: TimelineViewModelState
) => {
  if (!hasRelevantCollapsedStateUpdate(state)) {
    return;
  }

  const previousCollapsedSettings = extractCollapsedState(previousState);
  const currentCollapsedSettings = extractCollapsedState(state);

  if (isEqual(previousCollapsedSettings, currentCollapsedSettings)) {
    return;
  }

  const onViewSettingsUpdate =
    state.viewModelSourceData.viewInterface.timelineCallbacks
      .onViewSettingsUpdate;

  onViewSettingsUpdate(ViewIds.TIMELINE, currentCollapsedSettings, true);
};

export const buildSortedViewModelData = (
  sourceData: ViewModelSourceData
): TimelineSourceData => {
  const sortedSourceData = sortViewModelSourceDataHierarchy(sourceData);

  return {
    ...sortedSourceData,
    timelineViewModelData:
      timelineViewModelOperations.buildViewModelData(sortedSourceData),
  };
};

type ViewModelDataState = {
  sourceData: ViewModelSourceData;
  viewModelData: TimelineSourceData;
};

const isViewModelRelevant = (
  previous: ViewModelSourceData,
  next: ViewModelSourceData
): boolean =>
  (Object.keys(next) as (keyof ViewModelSourceData)[]).some(
    key => key !== 'sourceDataViewSettings' && previous[key] !== next[key]
  );

export const updateViewModelData = (
  previous: ViewModelDataState | null,
  sourceData: ViewModelSourceData
): ViewModelDataState => {
  if (!previous || isViewModelRelevant(previous.sourceData, sourceData)) {
    return { sourceData, viewModelData: buildSortedViewModelData(sourceData) };
  }
  return {
    sourceData,
    viewModelData: {
      ...previous.viewModelData,
      sourceDataViewSettings: sourceData.sourceDataViewSettings,
    },
  };
};

export const withUiState = ([state, commands, uiState]: [
  TimelineViewModelState,
  TimelineViewCommands,
  TimelineUiOnlyViewSettings,
]): TimelineRenderingState => ({
  ...state,
  commands,
  viewSettings: { ...state.viewSettings, ...uiState },
});

type RenderingDataLayerState = ViewModelDataState & {
  chartProperties: TimelineChartPropertiesState;
  chartWindow: ChartWindowArgs;
  mouseHover: MouseHoverEventStreamState;
  viewModelState: TimelineViewModelState;
};

type RenderingDataLayerAction =
  | { type: 'sourceDataReset'; sourceData: ViewModelSourceData }
  | {
      type: 'chartPropertiesChanged';
      chartProperties: Partial<TimelineChartPropertiesState>;
    }
  | { type: 'interactionActionDispatched'; action: Action<unknown> };

const resetRenderingData = (
  state: RenderingDataLayerState
): RenderingDataLayerState => ({
  ...state,
  viewModelState: timelineViewModelOperations.resetChartAndItemsRenderingData(
    state.viewModelState,
    [state.viewModelData, state.chartProperties, state.chartWindow]
  ),
});

const applyChartProperties = (
  state: RenderingDataLayerState,
  chartProperties: Partial<TimelineChartPropertiesState>
): RenderingDataLayerState =>
  resetRenderingData({
    ...state,
    chartProperties: { ...state.chartProperties, ...chartProperties },
  });

const applyChartWindow = (
  state: RenderingDataLayerState,
  chartWindow: ChartWindowArgs
): RenderingDataLayerState => resetRenderingData({ ...state, chartWindow });

const applyMouseHover = (
  state: RenderingDataLayerState,
  mouseHoverChanges: Partial<MouseHoverEventStreamState>
): RenderingDataLayerState => {
  const mouseHover = { ...state.mouseHover, ...mouseHoverChanges };
  return {
    ...state,
    mouseHover,
    viewModelState: timelineViewModelOperations.updateMouseHoverState(
      state.viewModelState,
      mouseHover
    ),
  };
};

const activateColumnsResize = (
  state: RenderingDataLayerState
): RenderingDataLayerState => applyMouseHover(state, { isResizeActive: true });

const deactivateColumnsResize = (
  state: RenderingDataLayerState
): RenderingDataLayerState => applyMouseHover(state, { isResizeActive: false });

const clearFocusedItem = (
  state: TimelineViewModelState
): TimelineViewModelState =>
  timelineViewModelOperations.updateFocusedItemsState(state, {
    focusedItemId: null,
  });

const startRenderingForExport = (
  state: TimelineViewModelState
): TimelineViewModelState =>
  timelineViewModelOperations.updateRenderForExportState(state, {
    renderingForExport: true,
  });

const stopRenderingForExport = (
  state: TimelineViewModelState,
  { isExporting }: { isExporting: boolean }
): TimelineViewModelState =>
  isExporting
    ? state
    : timelineViewModelOperations.updateRenderForExportState(state, {
        renderingForExport: false,
      });

const layerReducers: ReducerArray<RenderingDataLayerState> = [
  reducer(setChartWindowState, applyChartWindow),
  reducer(setUserDefinedLabelsColumnWidth, applyChartProperties),
  reducer(setTimelineMouseHover, applyMouseHover),
  reducer(setResizeIsActive, activateColumnsResize),
  reducer(setResizeIsInactive, deactivateColumnsResize),
];

const viewModelReducers: ReducerArray<TimelineViewModelState> = [
  reducer(
    setTimelineChartWindowScrollPosition,
    timelineViewModelOperations.updateChartWindowStateBasedOnChartWindowScrollPosition
  ),
  reducer(
    collapseExpandGroup,
    timelineViewModelOperations.handleCollapsedGroup
  ),
  reducer(
    collapseExpandAllGroups,
    timelineViewModelOperations.handleAllGroupsCollapsed
  ),
  reducer(
    collapseExpandFieldData,
    timelineViewModelOperations.handleCollapsedFieldData
  ),
  reducer(
    collapseExpandAllFieldData,
    timelineViewModelOperations.handleAllFieldDataCollapsed
  ),
  reducer(setHoveredItem, timelineViewModelOperations.updateHoveredItemsState),
  reducer(setFocusedItem, timelineViewModelOperations.updateFocusedItemsState),
  reducer(clearGraphSelection, clearFocusedItem),
  reducer(
    chartAreaScrollTopChanged,
    timelineViewModelOperations.updateVirtualScroll
  ),
  reducer(viewExportRequested, startRenderingForExport),
  reducer(setIsViewExporting, stopRenderingForExport),
];

const SCHEDULED_ACTION_TYPES: string[] = [
  setUserDefinedLabelsColumnWidth.type,
  setResizeIsInactive.type,
  setIsViewExporting.type,
];

const immediateActionCreators = [...layerReducers, ...viewModelReducers]
  .flatMap(({ trigger }) => ('actions' in trigger ? trigger.actions : []))
  .filter(({ type }) => !SCHEDULED_ACTION_TYPES.includes(type));

const findReducer = <State,>(reducers: ReducerArray<State>, type: string) =>
  reducers.find(
    ({ trigger }) =>
      'actions' in trigger &&
      trigger.actions.some(actionCreator => actionCreator.type === type)
  );

const applyInteractionReducer = (
  state: RenderingDataLayerState,
  { type, payload }: Action<unknown>
): RenderingDataLayerState => {
  const layerReducer = findReducer(layerReducers, type);
  if (layerReducer) {
    return layerReducer(state, payload);
  }
  const viewModelReducer = findReducer(viewModelReducers, type);
  return viewModelReducer
    ? {
        ...state,
        viewModelState: viewModelReducer(state.viewModelState, payload),
      }
    : state;
};

const updateRenderingData = (
  state: RenderingDataLayerState,
  action: RenderingDataLayerAction
): RenderingDataLayerState => {
  switch (action.type) {
    case 'sourceDataReset':
      return resetRenderingData({
        ...state,
        ...updateViewModelData(state, action.sourceData),
      });
    case 'chartPropertiesChanged':
      return applyChartProperties(state, action.chartProperties);
    case 'interactionActionDispatched':
      return applyInteractionReducer(state, action.action);
  }
};

const initRenderingData = (
  sourceData: ViewModelSourceData
): RenderingDataLayerState =>
  resetRenderingData({
    ...updateViewModelData(null, sourceData),
    chartProperties: INITIAL_CHART_PROPERTIES,
    chartWindow: DEFAULT_CHART_WINDOW_STATE,
    mouseHover: INITIAL_MOUSE_HOVER,
    viewModelState: DEFAULT_TIMELINE_VIEW_MODEL_STATE,
  });

const getRenderingDataLayerAction$ = (
  viewInstanceId: string
): Observable<RenderingDataLayerAction> => {
  const namespacedAction$ = action$.pipe(withNamespace(viewInstanceId));
  return merge(
    namespacedAction$.pipe(ofType(...immediateActionCreators)),
    namespacedAction$.pipe(
      ofType(setUserDefinedLabelsColumnWidth),
      debounceTime(LABELS_COLUMN_WIDTH_DEBOUNCE_TIME)
    ),
    namespacedAction$.pipe(
      ofType(setResizeIsInactive),
      debounceTime(COLUMNS_RESIZE_DEACTIVATION_DEBOUNCE_TIME)
    ),
    action$.pipe(withNamespace(ViewIds.TIMELINE), ofType(setIsViewExporting))
  ).pipe(
    map((action): RenderingDataLayerAction => ({
      type: 'interactionActionDispatched',
      action,
    }))
  );
};

const MemoizedTimeline = memo(Timeline);

const TimelineRenderingLayerComponent = ({
  viewInstanceId,
  settingsControlsHandlers,
  sourceData,
  uiOnlyViewSettings: { isLegendActive },
  commands,
  ...connectedComponentProps
}: TimelineRenderingLayerProps) => {
  const [state, dispatch] = useReducer(
    updateRenderingData,
    sourceData,
    initRenderingData
  );

  if (state.sourceData !== sourceData) {
    dispatch({ type: 'sourceDataReset', sourceData });
  }

  const viewCommands = useMemo(
    (): TimelineViewCommands & TimelineRenderingCommands => ({
      ...commands,
      setClientRect: clientRect =>
        dispatch({
          type: 'chartPropertiesChanged',
          chartProperties: { clientRect },
        }),
      resetLabelsColumnWidth: () =>
        dispatch({
          type: 'chartPropertiesChanged',
          chartProperties: { userDefinedLabelsColumnWidth: null },
        }),
      setContentContainer: getTimelineContentContainer =>
        dispatch({
          type: 'chartPropertiesChanged',
          chartProperties: { getTimelineContentContainer },
        }),
    }),
    [commands]
  );

  useEffect(() => {
    const subscription =
      getRenderingDataLayerAction$(viewInstanceId).subscribe(dispatch);
    return () => subscription.unsubscribe();
  }, [viewInstanceId]);

  const renderingState = useMemo(
    () => withUiState([state.viewModelState, commands, { isLegendActive }]),
    [state.viewModelState, commands, isLegendActive]
  );

  const previousRenderingStateRef = useRef<TimelineRenderingState | null>(null);

  const reportRenderingState = useEffectEvent(
    (renderingState: TimelineRenderingState) => {
      const previousRenderingState =
        previousRenderingStateRef.current ??
        withUiState([
          DEFAULT_TIMELINE_VIEW_MODEL_STATE,
          commands,
          { isLegendActive },
        ]);
      previousRenderingStateRef.current = renderingState;

      updateCollapsedSettingsFromState(previousRenderingState, renderingState);

      if (settingsControlsHandlers) {
        const { getSettingsControlsData, publishSettingsControlsData } =
          settingsControlsHandlers;

        const settingsData = getSettingsControlsData(
          previousRenderingState,
          renderingState
        );

        if (settingsData) {
          publishSettingsControlsData(settingsData);
        }
      }

      renderingState.viewModelSourceData.viewInterface.trackingCallbacks?.onViewModelReady?.();
    }
  );

  useEffect(() => {
    reportRenderingState(renderingState);
  }, [renderingState]);

  return (
    <MemoizedTimeline
      {...renderingState}
      {...connectedComponentProps}
      commands={viewCommands}
    />
  );
};

const TimelineRenderingLayer = memo(TimelineRenderingLayerComponent);

export { TimelineRenderingLayer };
